/*
 * pix-price-guard.js
 *
 * Mantém coerentes os valores do bloco de preço da página de produto.
 *
 * Bloco atual (snippets/price-pdp.liquid, "Pix em destaque"): três valores em elementos
 * próprios — preço cheio no cartão ([data-amme-card-value]), preço no Pix
 * ([data-amme-pix-value]) e parcela ([data-amme-installment]) — mais a linha do riscado
 * (.amme-price__compare, com [data-amme-compare-value] e o percentual [data-amme-off]).
 *
 * O app de opções de produto (Avis / aris-options) escreve no PDP o total da variante +
 * opções via innerHTML nos elementos `.price.price--large .price-item.price-item--last`
 * (preço) e `.price.price--large .price-item.price-item--regular` (riscado; vazio quando
 * igual ao preço). O snippet dá essas classes só ao preço cheio e ao riscado, então o app
 * nunca toca Pix nem parcela. Este script observa o(s) bloco(s) `#price-*` e, a cada
 * mutação, deriva do preço cheio EXIBIDO (fonte única): Pix = cheio x 0,95, parcela =
 * cheio / 3 e desconto = 1 - cheio / riscado (linha oculta se não houver riscado maior).
 * Guarda anti-loop: só escreve quando o texto muda, então cada gravação do app gera no
 * máximo um scan sem reescrita.
 *
 * Markup antigo (snippets/price.liquid com .price-item--sale.price-item--last dentro de um
 * `#price-*`, ex.: seção featured-product): o app reescreve o próprio span do Pix com o
 * preço cheio e apaga o rótulo "(no PIX)"; aqui reaplicamos o desconto sobre o valor
 * exibido (guarda: presença do rótulo) e recalculamos `.parcelamento-simples` a partir
 * dele. Mantido como fallback enquanto esse markup existir.
 */
(function () {
  'use strict';

  var PIX_RATE = 0.95; // 5% de desconto no PIX (markup antigo)
  var PIX_PERCENT = 95; // idem, em inteiro, para as contas em centavos do bloco atual
  var PARCELAS = 3; // parcelamento sem juros exibido pelo tema (igual ao snippet)
  var LABEL = ' <span style="font-weight: 500; font-size: 0.85em;">(no PIX)</span>'; // markup antigo
  var SELECTOR_LEGACY = '.price-item--sale.price-item--last';

  var currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  // "R$ 1.234,56" com espaço comum (o Intl usa NBSP), igual ao `money` do Liquid.
  var formatter = {
    format: function (value) {
      return currencyFormatter.format(value).replace(/\u00a0/g, ' ');
    },
  };

  function setText(el, text) {
    if (el && el.textContent.replace(/\s+/g, ' ').trim() !== text) el.textContent = text;
  }

  /*
   * Bloco atual: deriva Pix, parcela e desconto do preço cheio exibido.
   * Retorna false quando o bloco não tem o markup novo (cai no legado).
   */
  function syncPdpBlock(block) {
    var cardEl = block.querySelector('[data-amme-card-value]');
    if (!cardEl) return false;
    var full = parseMoney(cardEl.textContent);
    if (full == null || full <= 0) return true;
    // Contas em centavos inteiros, com arredondamento de meio para cima: reproduz o Liquid
    // (BigDecimal) do snippet, p.ex. R$ 167,30 x 0,95 = 15893,5 -> R$ 158,94.
    var cents = Math.round(full * 100);

    setText(block.querySelector('[data-amme-pix-value]'), formatter.format(Math.round((cents * PIX_PERCENT) / 100) / 100));
    setText(block.querySelector('[data-amme-installment]'), formatter.format(Math.round(cents / PARCELAS) / 100));

    var compareRow = block.querySelector('.amme-price__compare');
    if (compareRow) {
      var compareEl = compareRow.querySelector('[data-amme-compare-value]');
      var compare = compareEl ? parseMoney(compareEl.textContent) : null;
      var compareCents = compare == null ? 0 : Math.round(compare * 100);
      var show = compareCents > cents;
      if (compareRow.hidden === show) compareRow.hidden = !show;
      if (show) {
        var off = Math.round(((compareCents - cents) * 100) / compareCents);
        setText(compareRow.querySelector('[data-amme-off]'), off + '% off');
      }
    }
    return true;
  }

  // Sem símbolo de moeda: espelha o `money_without_currency` do Liquid ("1.234,56").
  var plainFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function parseMoney(text) {
    if (!text) return null;
    var cleaned = text.replace(/[^\d.,]/g, '').trim(); // mantém apenas dígitos, ponto e vírgula
    if (!cleaned) return null;
    // pt-BR: "." separador de milhar, "," separador decimal
    var value = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    return isNaN(value) ? null : value;
  }

  // --- Markup antigo (fallback) ---
  function applyPix(span) {
    // Guarda anti-loop: se já está no formato PIX, não reprocessa.
    if (/no\s*pix/i.test(span.textContent)) return;
    var raw = parseMoney(span.textContent);
    if (raw == null || raw <= 0) return;
    span.innerHTML = formatter.format(raw * PIX_RATE) + LABEL;
  }

  // Preço cheio (não-PIX, em reais) exibido num bloco `#price-*`, ou null. Prefere o span
  // visível (o tema alterna .price__regular/.price__sale). Se o valor já está no formato
  // PIX (com desconto), reverte dividindo por PIX_RATE para recuperar o preço cheio.
  function fullPriceFromBlock(block) {
    var spans = block.querySelectorAll('.price-item--sale.price-item--last');
    var span = null;
    for (var i = 0; i < spans.length; i++) {
      if (spans[i].offsetParent !== null) {
        span = spans[i];
        break;
      }
    }
    if (!span) span = spans[0];
    if (!span) return null;
    var value = parseMoney(span.textContent);
    if (value == null || value <= 0) return null;
    if (/no\s*pix/i.test(span.textContent)) value = value / PIX_RATE;
    return Math.round(value * 100) / 100; // evita arraste de ponto flutuante antes de dividir
  }

  // Reescreve `.parcelamento-simples` a partir do preço cheio exibido (fonte única), no mesmo
  // formato do Liquid ("ou 3x de R$ 86,33"). Guarda anti-loop: só escreve se o texto mudou —
  // a escrita dispara o observer, mas o scan seguinte encontra o valor igual e não reescreve.
  function updateInstallments(block) {
    var nodes = block.querySelectorAll('.parcelamento-simples');
    if (!nodes.length) return;
    var full = fullPriceFromBlock(block);
    if (full == null) return;
    var parcela = Math.round((full / PARCELAS) * 100) / 100;
    var text = 'ou ' + PARCELAS + 'x de R$ ' + plainFormatter.format(parcela);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent.replace(/\s+/g, ' ').trim() !== text) {
        nodes[i].textContent = text;
      }
    }
  }

  function scan() {
    var blocks = document.querySelectorAll('[id^="price-"]');
    for (var b = 0; b < blocks.length; b++) {
      if (syncPdpBlock(blocks[b])) continue;
      var spans = blocks[b].querySelectorAll(SELECTOR_LEGACY);
      for (var i = 0; i < spans.length; i++) applyPix(spans[i]);
      updateInstallments(blocks[b]);
    }
  }

  /*
   * Guarda dos preços nos CARDS de produto (carrosséis "Veja outros modelos" /
   * "Você pode gostar").
   *
   * Nos cards, o app (apo-style-products) reescreve tanto o preço de venda
   * (`.price-item--sale.price-item--last`) quanto o riscado (`s.price-item--regular`)
   * com os valores do produto ATUAL do PDP (ex.: R$ 129,00), em vez dos valores de
   * cada card — e remove o rótulo "(no PIX)". O disparo é preguiçoso (acontece quando o
   * card entra na viewport), por isso não basta corrigir uma vez no load.
   *
   * Os valores corretos ficam estampados por `price.liquid` em `data-amme-pix`
   * (preço x 0,95) e `data-amme-compare` (compare-at). O app só troca o texto, não os
   * atributos — então reaplicamos a partir deles sempre que divergirem. O PDP (bloco
   * `#price-*`) é ignorado: lá o total da variante+opções é legítimo e o desconto PIX é
   * tratado por `syncPdpBlock` / `applyPix` acima.
   */
  var CARD_PIX_SELECTOR = '.price-item--sale.price-item--last[data-amme-pix]';
  var CARD_COMPARE_SELECTOR = 's.price-item--regular[data-amme-compare]';

  function isInPdpBlock(el) {
    return !!el.closest('[id^="price-"]');
  }

  function fixCardPix(span) {
    if (isInPdpBlock(span)) return;
    var pix = span.getAttribute('data-amme-pix');
    if (!pix) return;
    // Guarda anti-loop: só reescreve se o valor correto ou o rótulo "(no PIX)" sumiram.
    if (span.textContent.indexOf(pix) === -1 || !/no\s*pix/i.test(span.textContent)) {
      span.innerHTML = pix + LABEL;
    }
  }

  function fixCardCompare(s) {
    if (isInPdpBlock(s)) return;
    var expected = s.getAttribute('data-amme-compare');
    if (expected && s.textContent.trim() !== expected.trim()) {
      s.textContent = expected;
    }
  }

  function fixCards() {
    var pixEls = document.querySelectorAll(CARD_PIX_SELECTOR);
    for (var i = 0; i < pixEls.length; i++) fixCardPix(pixEls[i]);
    var cmpEls = document.querySelectorAll(CARD_COMPARE_SELECTOR);
    for (var j = 0; j < cmpEls.length; j++) fixCardCompare(cmpEls[j]);
  }

  function init() {
    var containers = document.querySelectorAll('[id^="price-"]');

    if (containers.length) {
      scan(); // corrige o estado inicial (caso o app já tenha reescrito)

      // Observer síncrono: as guardas (texto igual / rótulo presente) garantem no máximo uma reescrita por gravação do app.
      var observer = new MutationObserver(scan);
      for (var i = 0; i < containers.length; i++) {
        observer.observe(containers[i], {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }
    }

    // Guarda dos cards: um observer no body cobre carrosséis carregados via JS
    // (related-products) e as reescritas preguiçosas do app na rolagem. A comparação
    // texto != atributo estampado é a guarda anti-loop (no máx. uma reescrita por
    // gravação do app).
    fixCards();
    var cardObserver = new MutationObserver(fixCards);
    cardObserver.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
